const getSelectableVariantAttributeKeys = (variants = []) => {
  const labelsByKey = {};

  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, attribute]) => {
      if (!labelsByKey[key]) {
        labelsByKey[key] = [];
      }
      labelsByKey[key].push(attribute?.label);
    });
  });

  return Object.keys(labelsByKey).filter((key) =>
    labelsByKey[key].some(
      (label) => typeof label === "string" && label.trim() !== "",
    ),
  );
};

const hasTrackedVariantQuantity = (variant) =>
  variant?.quantity !== undefined &&
  variant?.quantity !== null &&
  variant?.quantity !== "";

export const getVariantQuantity = (variant) => {
  if (!hasTrackedVariantQuantity(variant)) return null;

  const parsedQuantity = parseFloat(variant.quantity);
  return Number.isNaN(parsedQuantity) ? null : parsedQuantity;
};

export const isVariantOutOfStock = (variant) => {
  const quantity = getVariantQuantity(variant);
  return quantity !== null && quantity <= 0;
};

export const applySelectedVariantToProduct = (product, variant) => {
  if (!product || !variant) return product;

  const variantQuantity = getVariantQuantity(variant);
  const result = {
    ...product,
    price: parseFloat(variant.price),
    price_sale: parseFloat(variant.promo_price),
    active_sale: parseFloat(variant.promo_price) > 0,
    product_variant_id: variant.id,
    selectedVariantId: variant.id,
    selectedVariantSku: variant.sku,
    selectedVariantQuantity: variantQuantity,
    selectedVariant: variant,
    selectedAttributes: {
      ...variant.attributes,
    },
  };

  if (variant.price_wholesale !== undefined && variant.price_wholesale !== null) {
    const wholesaleVal = parseFloat(variant.price_wholesale);
    if (!isNaN(wholesaleVal)) {
      result.price_wholesale = wholesaleVal;
    }
  }

  return result;
};

export const isVariantSelectionComplete = (product) => {
  if (!product?.variants?.length) return true;

  const selectableKeys = getSelectableVariantAttributeKeys(product.variants);
  return selectableKeys.every(
    (key) => product.selectedAttributes?.[key]?.value !== undefined,
  );
};

export const getProductWithSingleOptionDefaults = (product) => {
  if (!product) return product;

  const productWithDefaults = { ...product };
  const hasVariants = product.variants?.length > 0;

  if (hasVariants) {
    const sizeAttributes = product.variants
      .map((variant) => variant.attributes?.size)
      .filter((attribute) => attribute?.value !== undefined);
    const uniqueSizeValues = [
      ...new Set(sizeAttributes.map((attribute) => attribute.value)),
    ];

    if (
      sizeAttributes.length === product.variants.length &&
      uniqueSizeValues.length === 1 &&
      product.selectedAttributes?.size?.value === undefined
    ) {
      const onlySize = sizeAttributes[0];
      productWithDefaults.selectedAttributes = {
        ...(product.selectedAttributes || {}),
        size: { ...onlySize },
      };

      const selectableKeys = getSelectableVariantAttributeKeys(product.variants);
      const allAttributesSelected = selectableKeys.every(
        (key) =>
          productWithDefaults.selectedAttributes?.[key]?.value !== undefined,
      );

      if (allAttributesSelected) {
        const selectedVariant = product.variants.find((variant) =>
          selectableKeys.every(
            (key) =>
              variant.attributes?.[key]?.value ===
              productWithDefaults.selectedAttributes[key].value,
          ),
        );

        if (selectedVariant) {
          Object.assign(
            productWithDefaults,
            applySelectedVariantToProduct(productWithDefaults, selectedVariant),
          );
        }
      }
    }
  }

  if (
    !hasVariants &&
    product.size?.length === 1 &&
    !product.sizeSelectedId?.length
  ) {
    const [onlySize] = product.size;
    productWithDefaults.sizeSelectedId = [onlySize.id];
    productWithDefaults.sizeName = onlySize.name;
    productWithDefaults.sizeValue = Number(onlySize.value);
  }

  if (product.flavor?.length === 1 && !product.flavorSelectedId?.length) {
    const [onlyFlavor] = product.flavor;
    productWithDefaults.flavorSelectedId = [onlyFlavor.id];
    productWithDefaults.flavorName = onlyFlavor.name;
    productWithDefaults.flavorValue =
      parseFloat(onlyFlavor.promo_price) > 0
        ? onlyFlavor.promo_price
        : onlyFlavor.value;
  }

  return productWithDefaults;
};
